import { Select, Tooltip } from '@forecastify/ui';
import { SCOPES, type Scope } from '@forecastify/shared';
import { useT, type MessageKey } from '../../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ THE CONTROL THAT REPLACES THE MOCKUP'S CHECKBOX.                        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `decisions.md` R-4 says the permission model is **ours, not the mockup's**, and this component is
 * the whole visible consequence. The mockup draws each permission as a checkbox — on or off — and
 * a checkbox cannot express *which rows*: its own catalogue had to invent `worklog.own`,
 * `worklog.team`, `worklog.all` and `worklog.edit.any` as four separate keys to say what one action
 * at four scopes says. Four keys drift; one action with a scope does not.
 *
 * So the row renders a three-way selector on the `none < own < workspace` ladder.
 *
 * ⚠️ **Three, not four.** `team` was on the ladder until `decisions.md` R-13 settled that a team is
 * not a concept in this product — it had no definition anywhere, the server resolved it to `own`,
 * and this control carried a warning saying so on every row where it was chosen.
 *
 * ⚠️ **A NATIVE `<select>`, deliberately.** `packages/ui`'s `Select` wraps the platform control,
 * which opens the OS picker on a phone. A Radix listbox in a popover looks better on a desktop and
 * is worse everywhere else, and this screen has twenty-five of them.
 */

const SCOPE_LABEL: Record<Scope, MessageKey> = {
  none: 'permissions.scope.none',
  own: 'permissions.scope.own',
  workspace: 'permissions.scope.workspace',
};

const SCOPE_HINT: Record<Scope, MessageKey> = {
  none: 'permissions.scope.none.hint',
  own: 'permissions.scope.own.hint',
  workspace: 'permissions.scope.workspace.hint',
};

/**
 * ⚠️ **An action whose ladder is about PROJECTS says so** — R-58, the product owner's own words for
 * the finance dashboard: *"All projects / Own projects / None"*. *Saját* alone would leave a reader
 * guessing whose rows "own" means; for a project action it means the projects one is a member of.
 */
const ACTION_SCOPE: Readonly<
  Record<
    string,
    { readonly label: Record<Scope, MessageKey>; readonly hint: Record<Scope, MessageKey> }
  >
> = {
  'project.finance': {
    label: {
      none: 'permissions.scope.none',
      own: 'permissions.scope.project.own',
      workspace: 'permissions.scope.project.workspace',
    },
    hint: {
      none: 'permissions.scope.none.hint',
      own: 'permissions.scope.project.own.hint',
      workspace: 'permissions.scope.project.workspace.hint',
    },
  },
};

export function ScopeSelect({
  action,
  actionLabel,
  value,
  disabled,
  onChange,
}: {
  readonly action: string;
  /** Already translated — the caller reads it out of the catalogue with a literal key. */
  readonly actionLabel: string;
  readonly value: Scope;
  readonly disabled: boolean;
  readonly onChange: (next: Scope) => void;
}) {
  const t = useT();
  const labels = ACTION_SCOPE[action]?.label ?? SCOPE_LABEL;
  const hints = ACTION_SCOPE[action]?.hint ?? SCOPE_HINT;

  return (
    <Tooltip content={t(hints[value])}>
      <Select
        /* ⚠️ The accessible name carries the ACTION, not just "scope". Twenty-five selects whose
         * label is the word "Hatókör" are twenty-five controls a screen reader cannot tell apart —
         * and this screen is exactly where somebody needs to. */
        aria-label={t('permissions.scope.label', { action: actionLabel })}
        data-action={action}
        value={value}
        disabled={disabled}
        onChange={(event) => {
          /* The cast is safe because the options below are the ladder and nothing else; a native
           * select cannot produce a value it was not given. */
          onChange(event.target.value as Scope);
        }}
        className="w-[156px]"
        options={SCOPES.map((scope) => ({ value: scope, label: t(labels[scope]) }))}
      />
    </Tooltip>
  );
}
